import { AxiosError } from "axios";

const MAX_ATTEMPTS = 2;

/**
 * A react-query `retry` predicate that repeats only what repeating can fix,
 * cheaply.
 *
 * `ERR_NETWORK` is the connection that never opened or died mid-flight. It
 * fails in milliseconds, so two more attempts are invisible to the SDR - that
 * is the two seconds of bad wifi that otherwise empties the list.
 *
 * A timeout is deliberately **not** retried. The client gives up after 20s
 * (`glencocoClientAPI`), so retrying twice would make the user wait 63s to
 * learn that something is wrong instead of 20s. A request that hangs that long
 * is not going to be fixed by asking again.
 *
 * A 4xx or 5xx is an answer. Repeating it changes nothing and doubles the load
 * on an endpoint that may already be struggling. Anything that is not an axios
 * error came from our own code - a bug in `queryFn` or `select` - and retrying
 * a bug just runs it again.
 *
 * Queries using this **must** also set `networkMode: "always"` and
 * `refetchOnReconnect: true`, in that pair.
 *
 * The first is required because once a retry is authorised react-query stops
 * rejecting: it sleeps and then parks the query until the browser reports
 * itself online and focused again (query-core/retryer.js:41,55,95). A dropped
 * connection is exactly when the browser reports offline, so without it this
 * predicate converts a loud failure into a spinner that never resolves.
 *
 * The second is required because the first silently switches it off -
 * `refetchOnReconnect` defaults to `networkMode !== "always"`
 * (query-core/queryClient.js:272). Leaving it off trades a spinner that
 * recovers for an error that does not: the query would sit failed until the
 * tab regained focus or the SDR reloaded.
 *
 * See retry-network-mode.test.tsx.
 */
// `Error` rather than `unknown`: react-query infers the query's TError from
// this signature, and widening it forces every consumer of those hooks to
// narrow an `unknown` error by hand.
export const retryOnTransportFailure = (failureCount: number, error: Error) => {
  if (failureCount >= MAX_ATTEMPTS) {
    return false;
  }

  const axiosError = error as AxiosError | undefined;

  return (
    axiosError?.isAxiosError === true &&
    axiosError.code === AxiosError.ERR_NETWORK
  );
};
